function hydrateSeriesSizes(disclosure) {
  if (!disclosure.open || disclosure.dataset.pnxSeriesHydrated) return;

  const panel = disclosure.querySelector(':scope > .pnx-sgrid__panel');
  const template = panel?.querySelector('template[data-pnx-series-size-template]');
  if (!template) return;

  panel.append(template.content.cloneNode(true));
  disclosure.dataset.pnxSeriesHydrated = 'true';
  template.remove();
}

document.addEventListener('toggle', (event) => {
  if (event.target instanceof HTMLDetailsElement && event.target.matches('.pnx-sgrid__disclosure')) {
    hydrateSeriesSizes(event.target);
  }
}, true);

document.querySelectorAll('.pnx-sgrid__disclosure[open]').forEach(hydrateSeriesSizes);

// Issue #75: exact/near WIDTH results. The original cards and commerce DOM stay intact.
function pnxParseDimensions(raw) {
  if (typeof raw !== 'string') return null;

  const normalized = raw.normalize('NFKC').replace(/[×XＸ]/g, 'x').trim();
  const value = normalized.split(/[:：]/).pop().trim();

  // Some vacuum bags carry thickness before width/length, e.g.
  // 「サイズ（厚×幅×長さ）：55μ×120×170mm」.
  // In that format the second/third numeric axes are width/length.
  const thicknessFirst = /厚\s*x\s*幅\s*x\s*長さ/i.test(normalized) || /^\s*\d+(?:\.\d+)?\s*(?:μ|um|µm?)\s*x/i.test(value);
  let width;
  let length;

  if (thicknessFirst) {
    const match3 = value.match(/(\d+(?:\.\d+)?)\s*(?:μ|um|µm?)?\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!match3) return null;
    width = Number(match3[2]);
    length = Number(match3[3]);
  } else {
    // Accept trailing notes such as:
    // 「90×170mm（底折込部27mm）」 / 「280×430（チャック下405）mm」.
    // Only the first width×length pair is used; note dimensions are ignored.
    const match2 = value.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!match2) return null;
    width = Number(match2[1]);
    length = Number(match2[2]);
  }

  return width > 0 && length > 0 && Number.isFinite(width) && Number.isFinite(length)
    ? { width, length } : null;
}

function pnxProductPath(raw) {
  if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//')) return null;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin || url.search || url.hash ||
        !/^\/(?:[a-z]{2}(?:-[a-zA-Z]{2})?\/)?products\/[^/]+\/?$/.test(url.pathname)) return null;
    return url.pathname;
  } catch (_) { return null; }
}

function pnxReadProductIndex(card) {
  const empty = { records: [], complete: false, unknown: 0 };
  const index = card.querySelector(':scope > template[data-pnx-product-index]');
  if (!index) return empty;
  try {
    const data = JSON.parse(index.dataset.pnxIndex || '');
    if (data.version !== 1 || !Array.isArray(data.items) || data.items.length > 250 ||
        !Number.isInteger(data.expected) || data.expected < 0 ||
        !Number.isInteger(data.seen) || data.seen !== data.items.length) return empty;
    const records = [];
    const seen = new Set();
    let unknown = 0;
    let duplicate = false;
    data.items.forEach((item) => {
      if (!Array.isArray(item) || item.length !== 4 || item.some((v) => typeof v !== 'string')) {
        unknown += 1; return;
      }
      const [handle, rawURL, rawCode, rawSize] = item;
      const url = pnxProductPath(rawURL);
      const dims = pnxParseDimensions(rawSize);
      if (!handle || !url || !dims) { unknown += 1; return; }
      if (seen.has(handle)) { duplicate = true; return; }
      seen.add(handle);
      // Unknown labels stay unknown. Do not synthesize a part number from dimensions or a SKU.
      const code = /^(?=.{1,40}$)(?=.*[0-9])[A-Za-z0-9 ._-]+$/.test(rawCode)
        ? rawCode : '品番は商品ページで確認';
      records.push({ handle, url, code, ...dims });
    });
    return {
      records, unknown,
      complete: data.page === 1 && data.seen === data.expected && !duplicate
    };
  } catch (_) { return empty; }
}

function pnxWidthMatches(records, width) {
  const exact = [];
  const near = [];
  records.forEach((record) => {
    const delta = Math.abs(record.width - width);
    if (delta === 0) exact.push(record);
    else if (delta <= 10) near.push(record);
  });
  const order = (a, b) => a.length - b.length || a.code.localeCompare(b.code, 'ja', { numeric: true });
  exact.sort(order);
  near.sort((a, b) => Math.abs(a.width - width) - Math.abs(b.width - width) || a.width - b.width || order(a, b));
  return { exact, near };
}

function pnxElement(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function pnxResultGroup(label, records, kind, width) {
  const group = pnxElement('section', `pnx-vfilter__match-group pnx-vfilter__match-group--${kind}`);
  group.append(pnxElement('h4', 'pnx-vfilter__match-heading', `${label}：${records.length}件`));
  if (!records.length) {
    group.append(pnxElement('p', 'pnx-vfilter__empty', '確認できた該当品番はありません。'));
    return group;
  }
  const list = pnxElement('ul', 'pnx-vfilter__products');
  group.append(list);
  let shown = 0;
  function appendBatch() {
    records.slice(shown, shown + 8).forEach((record) => {
      const li = pnxElement('li', 'pnx-vfilter__product');
      const link = pnxElement('a', 'pnx-vfilter__product-link');
      link.href = record.url;
      link.append(pnxElement('strong', 'pnx-vfilter__code', record.code));
      link.append(pnxElement('span', 'pnx-vfilter__dimension', `${record.width}×${record.length}mm`));
      const delta = record.width - width;
      link.append(pnxElement('span', 'pnx-vfilter__product-action',
        kind === 'near' ? `幅 ${delta > 0 ? '+' : ''}${delta}mm ｜ 商品を見る →` : '商品を見る →'));
      li.append(link); list.append(li);
    });
    shown = Math.min(shown + 8, records.length);
  }
  appendBatch();
  if (shown < records.length) {
    const more = pnxElement('button', 'pnx-vfilter__more');
    more.type = 'button';
    const sync = () => {
      more.textContent = shown < records.length ? `残り${records.length - shown}件を表示` : 'すべて表示しました';
      more.disabled = shown === records.length;
    };
    more.addEventListener('click', () => { appendBatch(); sync(); });
    sync(); group.append(more);
  }
  return group;
}

function initVacuumSeriesFilter(filter) {
  if (!(filter instanceof HTMLElement) || filter.dataset.pnxReady === 'products-v3') return;
  const section = filter.closest('[id^="shopify-section-"]');
  if (!section) return;
  const cards = [...section.querySelectorAll('.pnx-sgrid__card[data-pnx-series-handle]')];
  const makerOptions = filter.querySelector('[data-pnx-maker-options]');
  const seriesOptions = filter.querySelector('[data-pnx-series-options]');
  const widthOptions = filter.querySelector('[data-pnx-width-options]');
  const clear = filter.querySelector('[data-pnx-vfilter-clear]');
  const result = filter.querySelector('[data-pnx-filter-result]');
  if (!cards.length || !makerOptions || !seriesOptions || !widthOptions || !clear || !result) return;
  const models = cards.map((card) => ({
    card,
    handle: card.dataset.pnxSeriesHandle,
    title: card.dataset.pnxSeriesTitle || '',
    maker: card.dataset.pnxMaker || '',
    ...pnxReadProductIndex(card),
    wrapper: null,
    panel: null
  })).filter((model) => model.handle);
  if (!models.length) return;
  const makers = [...new Set(models.map((model) => model.maker).filter(Boolean))];
  const series = new Map(models.map((model) => [model.handle, model.title]));
  const seriesMaker = new Map(models.map((model) => [model.handle, model.maker]));
  const allWidths = [...new Set(models.flatMap((model) => model.records.map((record) => record.width)))].sort((a, b) => a - b);
  let selectedMaker = '*';
  let selectedSeries = '*';
  let selectedWidth = null;
  const makerButtons = new Map();
  const seriesButtons = new Map();
  const widthButtons = new Map();
  const seriesSelect = pnxElement('select', 'pnx-vfilter__select pnx-vfilter__select--series');
  seriesSelect.setAttribute('aria-label', seriesOptions.getAttribute('aria-label') || 'シリーズで絞り込む');
  const widthSelect = pnxElement('select', 'pnx-vfilter__select pnx-vfilter__select--width');
  widthSelect.setAttribute('aria-label', widthOptions.getAttribute('aria-label') || '幅で絞り込む');
  const grids = new Set(models.map((model) => model.card.parentElement));
  const groups = [...section.querySelectorAll('.pnx-sgrid')];
  const lead = filter.querySelector('.pnx-vfilter__lead');
  if (lead) lead.textContent = 'メーカーやシリーズを選び、幅を選ぶと該当品番と近い幅の商品を表示します。';
  const notice = pnxElement('p', 'pnx-vfilter__notice',
    '完全一致・近いサイズは幅のみの比較です。長さ・材質・厚み・使用条件は商品ページでご確認ください。');
  result.after(notice);
  clear.disabled = false; // Keep focus on the same control after resetting.

  function chip(label, kind, value, handler) {
    const button = pnxElement('button', `pnx-vfilter__chip pnx-vfilter__chip--${kind}`, label);
    button.type = 'button';
    button.dataset.pnxFilterValue = String(value);
    button.addEventListener('click', handler);
    return button;
  }

  function restore(model) {
    if (model.wrapper) model.wrapper.replaceWith(model.card);
    model.wrapper = null; model.panel = null;
  }

  function showProducts(model, matches) {
    if (!model.wrapper) {
      model.wrapper = pnxElement('div', 'pnx-vfilter__match-row');
      model.card.before(model.wrapper);
      model.wrapper.append(model.card);
      model.panel = pnxElement('aside', 'pnx-vfilter__matches');
      model.panel.setAttribute('aria-label', `${model.title}の該当品番`);
      model.wrapper.append(model.panel);
    }
    model.panel.replaceChildren();
    model.panel.append(pnxElement('h3', 'pnx-vfilter__matches-title', `幅 ${selectedWidth}mm の品番を選ぶ`));
    model.panel.append(pnxElement('p', 'pnx-vfilter__columns', '品番　／　幅×長さ'));
    model.panel.append(pnxResultGroup('幅が完全一致', matches.exact, 'exact', selectedWidth));
    model.panel.append(pnxResultGroup('近い幅（±10mm・完全一致を除く）', matches.near, 'near', selectedWidth));
    if (!model.complete || model.unknown) {
      model.panel.append(pnxElement('p', 'pnx-vfilter__incomplete',
        '全商品または一部の寸法を確認できていません。この結果だけでは該当なしと判断できません。シリーズ一覧でもご確認ください。'));
    }
    const originalLink = model.card.querySelector('.pnx-sgrid__primary');
    if (originalLink) {
      const link = pnxElement('a', 'pnx-vfilter__all-products', 'このシリーズの全商品を見る →');
      link.href = originalLink.href; model.panel.append(link);
    }
  }

  function apply() {
    let exact = 0; let near = 0; let incomplete = 0;
    const visibleSeries = new Set();
    const makerSource = models.filter((model) => selectedMaker === '*' || model.maker === selectedMaker);
    const source = makerSource.filter((model) => selectedSeries === '*' || model.handle === selectedSeries);
    const widths = new Set(source.flatMap((model) => model.records.map((record) => record.width)));
    seriesSelect.replaceChildren();
    [['*', 'すべて'], ...[...series].filter(([handle]) =>
      selectedMaker === '*' || seriesMaker.get(handle) === selectedMaker)].forEach(([value, label]) => {
      const option = pnxElement('option', '', label);
      option.value = value;
      seriesSelect.append(option);
    });
    seriesSelect.value = selectedSeries;
    widthSelect.replaceChildren();
    [null, ...allWidths.filter((width) => widths.has(width) || width === selectedWidth)].forEach((width) => {
      const option = pnxElement('option', '', width === null ? '指定なし' : String(width));
      option.value = width === null ? '*' : String(width);
      widthSelect.append(option);
    });
    widthSelect.value = selectedWidth === null ? '*' : String(selectedWidth);
    makerButtons.forEach((button, value) => button.setAttribute('aria-pressed', String(value === selectedMaker)));
    seriesButtons.forEach((button, value) => {
      button.setAttribute('aria-pressed', String(value === selectedSeries));
      button.hidden = value !== '*' && selectedMaker !== '*' && seriesMaker.get(value) !== selectedMaker;
    });
    widthButtons.forEach((button, value) => {
      button.setAttribute('aria-pressed', String(value === selectedWidth));
      button.hidden = value !== null && !widths.has(value) && value !== selectedWidth;
    });
    grids.forEach((grid) => grid.classList.toggle('pnx-vfilter__grid-results', selectedWidth !== null));
    models.forEach((model) => {
      const eligible =
        (selectedMaker === '*' || model.maker === selectedMaker) &&
        (selectedSeries === '*' || model.handle === selectedSeries);
      const matches = selectedWidth === null ? { exact: [], near: [] } : pnxWidthMatches(model.records, selectedWidth);
      const uncertain = !model.complete || model.unknown > 0;
      const show = eligible && (selectedWidth === null || selectedSeries !== '*' ||
        matches.exact.length > 0 || matches.near.length > 0 || uncertain);
      if (selectedWidth !== null && show) showProducts(model, matches);
      else restore(model);
      model.card.hidden = !show;
      if (model.wrapper) model.wrapper.hidden = !show;
      if (show) {
        visibleSeries.add(model.handle);
        exact += matches.exact.length; near += matches.near.length;
        if (uncertain) incomplete += 1;
      }
    });
    groups.forEach((group) => {
      const own = models.filter((model) => group.contains(model.card));
      if (own.length) group.hidden = own.every((model) => model.card.hidden);
    });
    const context = [];
    if (selectedMaker !== '*') context.push(selectedMaker);
    if (selectedSeries !== '*') context.push(series.get(selectedSeries) || selectedSeries);
    const prefix = context.length ? `${context.join('・')}：` : '';
    result.textContent = selectedWidth === null
      ? `${prefix}${visibleSeries.size}シリーズを表示。幅を選ぶと該当品番が表示されます。`
      : `${prefix}幅 ${selectedWidth}mm：完全一致 ${exact}件 ／ 近い幅 ${near}件（${visibleSeries.size}シリーズ）${incomplete ? ' ※未確認データを含むシリーズあり' : ''}`;
    clear.setAttribute('aria-disabled', String(selectedMaker === '*' && selectedSeries === '*' && selectedWidth === null));
    notice.hidden = selectedWidth === null;
  }

  makerOptions.replaceChildren(); seriesOptions.replaceChildren(seriesSelect); widthOptions.replaceChildren(widthSelect);
  [['*', 'すべて'], ...makers.map((maker) => [maker, maker])].forEach(([maker, label]) => {
    const button = chip(label, 'maker', maker, () => {
      selectedMaker = maker;
      if (selectedSeries !== '*' && maker !== '*' && seriesMaker.get(selectedSeries) !== maker) selectedSeries = '*';
      apply();
    });
    makerButtons.set(maker, button); makerOptions.append(button);
  });
  [['*', 'すべて'], ...series].forEach(([handle, title]) => {
    const button = chip(title, 'series', handle, () => { selectedSeries = handle; apply(); });
    seriesButtons.set(handle, button); seriesOptions.append(button);
  });
  [null, ...allWidths].forEach((width) => {
    const button = chip(width === null ? '指定なし' : String(width), 'width', width === null ? '*' : width, () => {
      selectedWidth = width === selectedWidth ? null : width; apply();
    });
    widthButtons.set(width, button); widthOptions.append(button);
  });
  seriesSelect.addEventListener('change', () => {
    selectedSeries = seriesSelect.value;
    apply();
  });
  widthSelect.addEventListener('change', () => {
    selectedWidth = widthSelect.value === '*' ? null : Number(widthSelect.value);
    apply();
  });
  clear.addEventListener('click', () => {
    selectedMaker = '*';
    selectedSeries = '*';
    selectedWidth = null;
    apply();
    if (window.matchMedia?.('(max-width: 749px)').matches) makerButtons.get('*')?.focus();
  });
  filter.dataset.pnxReady = 'products-v3';
  apply();
}

// Issue #103: on ?page=2+ the server reads each series with the hub's page number, so the card
// indexes come back empty (page 2). Refetch the same section without ?page (Sleek facets.js builds
// its URL the same way) and copy only the index attribute values into the current cards.
// The three pnxSeries* functions are byte-identical in pnx-series-grid.js and pnx-series-finder.js.
function pnxSeriesIndexStale(section) {
  return [...section.querySelectorAll('.pnx-sgrid__card[data-pnx-series-handle]')].some((card) => {
    const index = card.querySelector(':scope > template[data-pnx-product-index]');
    try {
      const page = JSON.parse(index?.getAttribute('data-pnx-index') || '').page;
      return Number.isInteger(page) && page >= 2;
    } catch (_) { return false; }
  });
}

// Resolves to the same section parsed from the page-less Section Rendering response, or null.
// Never rejects; one request, no retry.
function pnxSeriesFetchSection(section) {
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.delete('page');
  url.searchParams.set('section_id', section.id.replace(/^shopify-section-/, ''));
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  return fetch(url.toString(), { credentials: 'same-origin', signal: controller.signal })
    .then((response) => {
      if (!response.ok || !(response.headers.get('content-type') || '').includes('text/html')) return null;
      return response.text().then((html) =>
        new DOMParser().parseFromString(html, 'text/html').getElementById(section.id));
    })
    .catch(() => null)
    .finally(() => clearTimeout(timer));
}

// [[current template, fetched index string], ...] when every card pairs up, or null. Changes nothing.
function pnxSeriesIndexPlan(section, fetched) {
  const read = (root) => {
    const cards = new Map();
    for (const card of root.querySelectorAll('.pnx-sgrid__card[data-pnx-series-handle]')) {
      const key = JSON.stringify([card.getAttribute('data-pnx-series-handle'), card.getAttribute('data-pnx-series-title')]);
      if (cards.has(key)) return null;
      cards.set(key, card.querySelector(':scope > template[data-pnx-product-index]'));
    }
    return cards;
  };
  try {
    const current = read(section);
    const next = fetched ? read(fetched) : null;
    if (!current || !next || !current.size || current.size !== next.size) return null;
    const plan = [];
    for (const [key, template] of current) {
      const source = next.get(key);
      if (!template || !source) return null;
      const value = source.getAttribute('data-pnx-index');
      const data = JSON.parse(value || '');
      const own = JSON.parse(template.getAttribute('data-pnx-index') || '');
      if (!data || !own || data.page !== 1 || data.version !== own.version) return null;
      plan.push([template, value]);
    }
    return plan;
  } catch (_) { return null; }
}

// 1st page: synchronous init as before. Stale index (page 2+): one refetch, then init once either way.
function initVacuumSeriesFilters(scope = document) {
  const filters = [...(scope.querySelectorAll?.('[data-pnx-vacuum-series-filter]') || [])];
  if (scope instanceof HTMLElement && scope.matches('[data-pnx-vacuum-series-filter]')) filters.push(scope);
  filters.forEach((filter) => {
    const refresh = filter.dataset.pnxIndexRefresh;
    if (refresh === 'pending') return;
    const section = filter.closest('[id^="shopify-section-"]');
    if (refresh === 'done' || refresh === 'failed' || !section || !pnxSeriesIndexStale(section)) {
      initVacuumSeriesFilter(filter);
      return;
    }
    filter.dataset.pnxIndexRefresh = 'pending';
    pnxSeriesFetchSection(section).then((fetched) => {
      const plan = pnxSeriesIndexPlan(section, fetched);
      if (plan) plan.forEach(([template, value]) => template.setAttribute('data-pnx-index', value));
      filter.dataset.pnxIndexRefresh = plan ? 'done' : 'failed';
      if (filter.isConnected) initVacuumSeriesFilter(filter);
    });
  });
}

initVacuumSeriesFilters(document);
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) initVacuumSeriesFilters(event.target);
});

// Issue #147: ‹ › buttons for the per-group horizontal rows (only on shelves with pnx.series_scroll).
// Without JS the rows still scroll by touch, trackpad and scrollbar; the buttons stay hidden.
function pnxSgridScrollStep(row) {
  const left = row.getBoundingClientRect().left;
  const card = [...row.children].find((el) => el.getBoundingClientRect().right > left + 1);
  if (!card) return row.clientWidth;
  const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
  return card.getBoundingClientRect().width + gap;
}

function initSgridScrollRow(row) {
  if (row.dataset.pnxScrollReady) return;
  row.dataset.pnxScrollReady = 'true';
  const btns = row.parentElement?.querySelector(':scope > [data-pnx-sgrid-btns]');
  const prev = btns?.querySelector('[data-pnx-sgrid-prev]');
  const next = btns?.querySelector('[data-pnx-sgrid-next]');
  if (!btns || !prev || !next) return;

  const update = () => {
    const max = row.scrollWidth - row.clientWidth;
    btns.hidden = max <= 1;
    prev.disabled = row.scrollLeft <= 1;
    next.disabled = row.scrollLeft >= max - 1;
  };
  const move = (dir) => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    row.scrollBy({ left: dir * pnxSgridScrollStep(row), behavior: reduce ? 'auto' : 'smooth' });
  };
  prev.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));
  row.addEventListener('scroll', update, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(update).observe(row);
  update();
}

function initSgridScrollRows(scope = document) {
  const rows = [...(scope.querySelectorAll?.('[data-pnx-sgrid-scroll]') || [])];
  if (scope instanceof HTMLElement && scope.matches('[data-pnx-sgrid-scroll]')) rows.push(scope);
  rows.forEach(initSgridScrollRow);
}

initSgridScrollRows(document);
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) initSgridScrollRows(event.target);
});
